import { Boundaries } from "@planzone/core";
import { project } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { reduceProject } from "../../common/project";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  { planzoneId }: Boundaries["retrievePlanzoneLogs"]["request"],
): State {
  const index = state.findIndex((req) => req.id === planzoneId);
  return index === -1
    ? [
        ...state,
        {
          request: setRequestStateToPending(),
          id: planzoneId,
          logs: [],
          pages: 0,
        },
      ]
    : [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ];
}

function handleSuccess(
  state: State,
  {
    logs,
    planzoneId,
    pages,
    pageIndex,
  }: Boundaries["retrievePlanzoneLogs"]["success"],
): State {
  const index = state.findIndex((req) => req.id === planzoneId);
  return index === -1
    ? [
        ...state,
        {
          request: setRequestStateToSuccess(),
          id: planzoneId,
          logs,
          pages,
        },
      ]
    : [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          pages,
          logs: pageIndex === 0 ? logs : [...state[index].logs, ...logs],
        },
        ...state.slice(index + 1),
      ];
}

function handleFailure(
  state: State,
  id: Boundaries["retrievePlanzoneLogs"]["failure"],
): State {
  const index = state.findIndex((req) => req.id === id);
  return index === -1
    ? [
        ...state,
        {
          request: setRequestStateToFailure(),
          id,
          logs: [],
          pages: 0,
        },
      ]
    : [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToFailure(),
        },
        ...state.slice(index + 1),
      ];
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrievePlanzoneLogsRequested]: handleRequest,
    [events.retrievePlanzoneLogsSucceeded]: handleSuccess,
    [events.retrievePlanzoneLogsFailed]: handleFailure,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
